<template>
  <g
    class="HeatmapChart HeatmapChart--vertical"
    :class="[`HeatmapChart--${alignValue}Align`]"
    :transform="`translate(${left}, ${top})`"
  >
    <g
      class="HeatmapChart-content"
      :transform="`translate(${viewboxPosition.x + paddingX}, ${paddingY})`"
    >
      <g
        v-for="(rect, i) in rects"
        class="HeatmapChart-rect"
        :transform="`translate(${rect.bandPos}, ${rect.lengthPos})`"
        :key="i"
      >
        <template
          v-for="(child, j) in rect.children"
          :key="`rect-${i}-${j}-${child.fill}`"
        >
          <datalas-rect
            :x="child.bandPos"
            :y="child.lengthPos"
            :width="child.bandwidth"
            :height="child.length"
            :datum="child.datum"
            :fill="child.fill"
            @datum:mouseover="$emit('datum:mouseover', $event)"
            @datum:mouseout="$emit('datum:mouseout', $event)"
            @datum:click="$emit('datum:click', $event)"
          />
        </template>
      </g>
    </g>

    <teleport v-if="gridMounted" :to="`#${gridKey}`">
      <DatalasFlexibleGrid
        v-bind="gridLayout"
        :viewboxPosition="viewboxPosition"
        :left="left"
        :chartHeight="widgetContentWrapperRect.height"
        :grid-key="gridKey"
        @update:axisSize="handleAxisSizeUpdate"
      />
    </teleport>
  </g>
</template>

<script>
import Layer from './layer.vue';
import DatalasFlexibleGrid from './flexible-grid.vue';

export default {
  name: 'DatalasHeatmap',
  components: { DatalasFlexibleGrid },
  extends: Layer,

  props: {
    align: {
      type: String,
    },
    normalize: {
      type: Boolean,
      default: false,
    },

    dimensions: {
      type: Array,
      required: true,
      validator: (val) => {
        const length = val.find((d) => d.dimension === 'length');
        const fill = val.find((d) => d.dimension === 'fill');
        const bands = val.filter((d) => d.dimension === 'band');

        return length && fill && bands.length;
      },
    },
    viewboxPosition: { type: Object, default: () => ({ x: 0, y: 0 }) },
    widgetContentWrapperRect: {
      type: Object,
      default: () => ({ width: 0, height: 0 }),
    },
  },

  emits: [
    'datum:mouseover',
    'datum:mouseout',
    'datum:click',
    'update:axisSize',
    'update:layoutTotalBandwidth',
  ],

  data: () => ({
    xDimension: 'band',
    yDimension: 'length',

    marginLeftCalculated: 0,
  }),

  computed: {
    layoutTotalBandwidth() {
      const padding = this.paddingX * 2;
      const margin = this.marginRight + this.marginLeft;
      const responsiveBandwidth =
        (this.widgetContentWrapperRect?.width - padding - margin) /
        this.nestedData.length;
      const minBandwidth = 40;

      const chartStep =
        responsiveBandwidth > minBandwidth ? responsiveBandwidth : minBandwidth;

      const projectedTotalBandwidth = this.nestedData.length * chartStep;

      const totalBandwith =
        projectedTotalBandwidth > this.widgetContentWrapperRect?.width
          ? projectedTotalBandwidth
          : this.totalBandwidth;

      this.$emit('update:layoutTotalBandwidth', totalBandwith);
      return totalBandwith;
    },
    length() {
      return this.dimensions.find((d) => d.dimension === 'length').transform;
    },
    fill() {
      return this.dimensions.find((d) => d.dimension === 'fill').transform;
    },
    bands() {
      return this.dimensions
        .filter((d) => d.dimension === 'band')
        .map((d) => d.transform);
    },

    totalBandwidth() {
      return this.width;
    },
    totalLength() {
      return this.height;
    },

    lengthTransform() {
      return this.transforms[this.length];
    },
    bandTransforms() {
      return this.bands.map((b) => this.transforms[b]);
    },

    fillTransform() {
      return this.transforms[this.fill];
    },
    fillScale() {
      const { options } = this.fillTransform;

      return this.$d3
        .scaleLinear()
        .range(options.scheme)
        .domain([this.min, this.max]);
    },

    nestedData() {
      const tree = this.$d3.nest().rollup((items) => items);

      this.bandTransforms.forEach((b) => {
        tree.key((d) => d[b.field]);
      });

      return tree.entries(this.data);
    },

    xDomain() {
      return this.nestedData.map((category) => category.key);
    },
    yDomain() {
      const [firstCategoryValues] = this.nestedData || [];

      return firstCategoryValues && firstCategoryValues.value
        ? firstCategoryValues.value.map((value) => value.values)
        : [];
    },

    layoutTree() {
      return this.getLayoutTree(this.nestedData, this.layoutTotalBandwidth);
    },
    rects() {
      const lastIndex = this.bandTransforms.length - 1;

      return this.getFieldLayout(this.bandTransforms[lastIndex].field);
    },
    gridLayout() {
      const grid = { ...this.grid };

      grid.axes = grid.axes.map((axis) => {
        const props = { ...axis.props };
        const { scale } = props;

        if (scale === this.length) {
          const lengthScale = this.getLengthScale(this.yDomain);

          props.scale = lengthScale;
        } else {
          const { field, transformer } = this.transforms[scale];
          const layout = this.getFieldLayout(field);
          props.scale = transformer(
            layout.map((l) => l.value),
            [0, this.layoutTotalBandwidth],
          );
        }

        props.height = this.height;
        props.width = this.width;

        return { ...axis, props };
      });

      return grid;
    },
    min() {
      const { min } = this.lengthTransform.options;

      return min === undefined || min === null
        ? Math.min(0, this.$d3.min(this.data, this.lengthAccessor))
        : min;
    },
    max() {
      const { max } = this.lengthTransform.options;

      if (max === undefined || max === null) {
        const getVals = (tree) =>
          tree.reduce(
            (acc, { values, value }) =>
              acc.concat(values ? getVals(values) : [value]),
            [],
          );
        const rects = getVals(this.nestedData);

        return this.$d3.max(rects.map((rect) => this.getLengthMax(rect)));
      }

      return max;
    },
    range() {
      const range = [0];
      range.push(this.totalLength);

      return range;
    },

    // overwrite with calculated value based on rendered left axis width
    // when no marginLeft specified
    left() {
      const marginLeft =
        this.context.marginLeft === undefined
          ? this.marginLeftCalculated
          : this.marginLeft;

      return marginLeft || 0;
    },
  },

  methods: {
    lengthAccessor(item) {
      return item[this.lengthTransform.field];
    },
    getLengthMax(items) {
      return this.$d3.max(items, this.lengthAccessor);
    },

    getLengthScale(domain) {
      return this.lengthTransform.transformer(domain, [this.height, 0]);
    },

    getLayoutTree(data, bandwidth, level = 0) {
      const scale = this.bandTransforms[level].transformer(
        data.map((d) => d.key),
        [0, bandwidth],
      );

      return data.map((d) => this.getGroupLayout(d, scale, level));
    },
    getGroupLayout(item, bandScale, level) {
      const bandwidth = bandScale.bandwidth();

      const lengthScale = this.getLengthScale(this.yDomain);
      const group = {
        bandPos: bandScale(item.key),
        lengthPos: 0,
        field: this.bandTransforms[level].field,
        bandwidth,
      };

      group.children = item.value
        ? this.getBarLayout(item.value, bandwidth, lengthScale)
        : this.getLayoutTree(item.values, bandwidth, level + 1);

      return group;
    },
    getBarLayout(bar, bandwidth, lengthScale) {
      const items = bar.slice();

      const { legendField = 'values' } = this.context;

      return items.reduce((acc, datum) => {
        const scaled = lengthScale(datum[legendField]);
        const lengthPos = scaled;
        const length = lengthScale.bandwidth();

        const fillKey = datum.data_column;
        const fill = this.fillScale(fillKey);

        return acc.concat([
          {
            lengthPos,
            bandwidth,
            length,

            fill,

            datum,
          },
        ]);
      }, []);
    },
    getFieldLayout(field) {
      const getVal = (o) =>
        o.children ? getVal(o.children[0]) : o.datum[field];
      const getFieldLayout = ({ items, bandPos }, item) => {
        if (item.field === field) {
          items.push({
            bandPos: bandPos + item.bandPos,
            bandwidth: item.bandwidth,
            lengthPos: item.lengthPos,
            children: item.children,
            value: getVal(item.children[0]),
          });
        } else if (item.children) {
          item.children.forEach((child) => {
            getFieldLayout({ items, bandPos: bandPos + item.bandPos }, child);
          });
        }

        return { items, bandPos };
      };

      return this.layoutTree.reduce(getFieldLayout, { items: [], bandPos: 0 })
        .items;
    },

    handleAxisSizeUpdate(event) {
      if (event.orient === 'left') {
        this.marginLeftCalculated = event.value?.width;
      }

      this.$emit('update:axisSize', event);
    },
  },
};
</script>
